import * as Sentry from '@sentry/browser';
import { captureGenericError } from './sentryCapture';
import { GCInstant } from 'src/lib/gcinstant';
import { ReplicantError } from '@play-co/replicant';

import StateObserver from 'src/StateObserver';
import { getReduxState } from './sentryForRedux';
import { track } from './analytics/utils';

// The majority of unhandled FB errors come from the sync postSessionScore.
// Internally it is a promise, but the SDK does not expose it so we can't handle them.
const MUTED_FB_ERRORS = [
  // Any FB call can fail when a client loses connection.
  'NETWORK_FAILURE',
  // The three main categories of unknown errors:
  // "could be network failure", no response data, tournaments fail to post to timeline.
  'UNKNOWN',
  // Some clients with old versions of android do not support the tournament dialog
  // Resulting in a CLIENT_UNSUPPORTED_OPERATION with
  // Client does not support the message: showgenericdialogasync
  'CLIENT_UNSUPPORTED_OPERATION',
  // Tournaments can sometimes hang on postSessionScore
  // resulting in the failure of all subsequent requests.
  'PENDING_REQUEST',
  // The tournament API can get rate limited if a client lets the post happen
  // on every building upgrade instead of being fast enough to batch them.
  'RATE_LIMITED',
  // It may be that someone closed the dialog on the tournament
  // screen, which will be reported as unhandled rejection here. We have no
  // access to the promise, and worse have no way to differentiate between this
  // and other unhandled user errors.
  'USER_INPUT',
  // For some reason postSessionScore can lead to a SAME_CONTEXT error.
  'SAME_CONTEXT',
];

export function addSentryContext() {
  Sentry.configureScope((scope) => {
    scope.setUser({
      id: GCInstant.playerID,
      username: GCInstant.playerName,
    });
  });
}

/**
 * Start transaction that you wan to track
 *
 * @example
 *
 * const transaction = Sentry.startTransaction({ name: "test-transaction" });
 * const span = transaction.startChild({ op: "functionX" }); // This function returns a Span
 *
 * exampleFunctionCall();
 *
 * span.finish(); // Remember that only finished spans will be sent with the transaction
 * transaction.finish(); // Finishing the transaction will send it to Sentry
 */
type TransactionOP = 'action' | 'async-getter' | 'assets';
type EventNames =
  | 'species-and-decorations'
  | 'duo-send-response'
  | 'duo-send-invite';

export function startTransaction(opts: { name: string; op?: TransactionOP }) {
  return Sentry.startTransaction(opts);
}

export async function measurePerformance<T>(
  opts: { op: TransactionOP; name: EventNames },
  promise: Promise<T>,
  parent?: ReturnType<typeof startTransaction>,
): Promise<T> {
  const { name, op } = opts;

  // The equivalent to `name` for a span is `description`
  const span =
    parent?.startChild({ description: name, op }) ?? startTransaction(opts);

  let status: Sentry.SpanStatusType = 'ok';
  const start = StateObserver.now();

  try {
    return await promise;
  } catch (err) {
    status = 'unknown_error';

    // Rethrow the error to keep the flow.
    throw err;
  } finally {
    track('PerformanceEvent', {
      op,
      name,
      durationMs: StateObserver.now() - start,
    });
    span.setStatus(status);
    span.finish();
  }
}

/**
 * Use in experimental situations where we temporarily need strong visibility.
 */
export function captureFacebookError(
  err: Error | { code: string; message: string },
) {
  if (err instanceof Error) {
    Sentry.captureException(err);
  } else {
    // Silence user input and rate limit errors
    if (err.code === 'USER_INPUT' || err.code === 'RATE_LIMITED') {
      return;
    }

    // Use an Error with a custom name for nice display in Sentry
    const exception = Error(`${err.code} (${err.message})`);
    exception.name = 'FacebookError';
    (exception as any).framesToPop = 1;

    // Prevent different error codes from being combined together
    Sentry.withScope((scope) => {
      scope.setFingerprint(['{{ default }}', err.code]);
      Sentry.captureException(exception);
    });
  }
}

export function captureReplicantError(err: ReplicantError) {
  // Use an Error with a custom name for nice display in Sentry
  const exception = Error(`${err.code} (${err.message})`);
  exception.name = 'ReplicantError';
  (exception as any).framesToPop = 1;

  // Prevent different error codes from being combined together
  Sentry.withScope((scope) => {
    scope.setFingerprint(['{{ default }}', err.code, err.subCode || '']);

    // In production, err.message is the requestId and we can correlate it with the server-side
    // error based on the requestId.
    if (!process.env.IS_DEVELOPMENT) {
      scope.setTag('requestId', err.message);
    }

    Sentry.captureException(exception);
  });
}

if (!process.env.SENTRY_DSN) {
  // Otherwise, we won't be able to see errors locally
  throw Error('process.env.SENTRY_DSN must be set');
}

type TransactionConfig = {
  baseSampleRate: number;
  traceSampleRate: Record<string, number | boolean>;
};

// Trace sampling only applies to transactions and not their children spans.
// There is inheritance and precedence at play:
// https://docs.sentry.io/platforms/javascript/configuration/sampling/#inheritance
const tracesConfig: TransactionConfig = {
  baseSampleRate: process.env.SENTRY_ENVIRONMENT === 'production' ? 0.0 : 1.0,
  traceSampleRate: {
    'visit-start-sequence': 0.0,
    'visit-end-sequence': 0.0,
    'duo-send-invite': 0.0,
    'duo-send-response': 0.0,
  },
};

Sentry.init({
  dsn: process.env.SENTRY_DSN,
  environment: process.env.SENTRY_ENVIRONMENT,
  attachStacktrace: true,

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // For prod we will capture 0%, it could be adjusted in future
  tracesSampleRate: tracesConfig.baseSampleRate,

  tracesSampler: (context) => {
    const transactionName = context.transactionContext.name;

    // If the transaction doesn't have a specified sample rate
    // it will default to tracesSampleRate
    return tracesConfig.traceSampleRate[transactionName];
  },

  integrations: [new Sentry.BrowserTracing()],

  release:
    process.env.SENTRY_ENVIRONMENT === 'production'
      ? process.env.SENTRY_PROJECT + '@' + process.env.APP_VERSION
      : undefined,

  // Truncate breadcrumbs to avoid exceeding Sentry's 100KB request size limit:
  beforeBreadcrumb: (bc) => {
    // Filter out Amplitude request breadcrumbs:
    if (bc.type === 'http' && bc.data?.url === 'https://api.amplitude.com') {
      return null;
    }

    // Truncate long console messages:
    if (bc.category === 'console' && (bc.message?.length ?? 0) > 1000) {
      if (!bc.data) bc.data = {};
      bc.data.arguments = [];
      bc.message = bc.message?.substr(0, 997) + '...';
    }
    // Truncate long URLs like asset data URLs:
    else if (bc.type === 'http' && bc.data?.url?.length > 1000) {
      if (!bc.data) bc.data = {};
      bc.data.url = bc.data.url?.substr(0, 997) + '...';
    }

    return bc;
  },

  beforeSend: (event, hint) => {
    if (!process.env.SENTRY_ENVIRONMENT) {
      // Locally, log the event to the console for easy inspection
      console.error(hint.originalException || hint.syntheticException);

      // Locally, make sure we discard the event by returning null
      return null;
    }

    // TODO Try to remove from 2020-12-01
    // Detect Facebook unhandled rejections that we just can't control and don't report them to Sentry.
    if (isUnhandledPromiseRejectionFromFBSDK(event, hint)) {
      return null;
    }

    // Detect "Trying to invoke <> while state is out of sync" and don't report it to Sentry
    if (isInvokeWhileOutOfSyncPromiseRejection(hint)) {
      return null;
    }

    if (!event.extra) {
      event.extra = {};
    }

    const { user, ...state } = getReduxState() || StateObserver.getState();
    event.extra.redux = state;
    event.extra.replicant = user;

    // TODO Enable this once we've added public/private fields
    // event.extra.friends = friends;

    // Sentry requires we return the event to them in order to send
    return event;
  },
});

function isInvokeWhileOutOfSyncPromiseRejection(hint: Sentry.EventHint) {
  const originalException = hint.originalException as any;
  if (
    originalException?.['code'] === 'replication_error' &&
    originalException?.['subCode'] === 'invoking_while_out_of_sync'
  ) {
    return true;
  }

  return false;
}

function isUnhandledPromiseRejectionFromFBSDK(
  event: Sentry.Event,
  hint: Sentry.EventHint,
) {
  if (event.exception?.values) {
    for (const exception of event.exception.values) {
      // Only care about unhandled rejections
      if (exception.mechanism?.type === 'onunhandledrejection') {
        // If we have a stacktrace and the call originates from FB SDK, then
        // ignore this promise rejection.
        const stackframes = exception.stacktrace?.frames;

        if (stackframes && stackframes.length > 0) {
          const lastFrame = stackframes[stackframes.length - 1];

          // If the rejection originates from the FB SDK directly.
          if (lastFrame.filename?.includes('fbinstant.beta.js')) {
            return true;
          }
        }
      }
    }
  }

  const originalException = hint.originalException as any;
  const code = originalException?.code;
  if (code && MUTED_FB_ERRORS.includes(code)) {
    return true;
  }

  // Alternatively, we've errors for users closing an FB dialog,
  // but the error code is not USER_INPUT.
  if (originalException?.mesage === 'User close dialog') {
    return true;
  }

  return false;
}

export function debugBreadcrumb(message: string) {
  Sentry.addBreadcrumb({
    category: 'debug',
    level: 'info',
    type: 'debug',
    message: message,
    data: {
      'replicant.now': new Date(StateObserver.now()),
      'Date.now': new Date(),
    },
  });
}

// Defined in a leaf module so Redux slices can import it without closing a cycle.
export { captureGenericError };
